import { useRef, useState, type ReactNode, type CSSProperties, type TouchEvent, type MouseEvent } from 'react'

export interface SwipeRowProps {
  children: ReactNode
  onEdit?: () => void
  onDelete?: () => void
  // Confirmation text shown before deleting (window.confirm). Defaults to a generic prompt.
  deleteConfirmText?: string
}

const ACTION_WIDTH = 64 // px per action button

export function SwipeRow({ children, onEdit, onDelete, deleteConfirmText }: SwipeRowProps) {
  const actionsCount = (onEdit ? 1 : 0) + (onDelete ? 1 : 0)
  const maxOffset = ACTION_WIDTH * actionsCount
  const [offset, setOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startX = useRef<number | null>(null)
  const startOffset = useRef(0)

  const onTouchStart = (e: TouchEvent) => {
    startX.current = e.touches[0].clientX
    startOffset.current = offset
    setIsDragging(true)
  }
  const onTouchMove = (e: TouchEvent) => {
    if (startX.current == null) return
    const dx = e.touches[0].clientX - startX.current
    const next = Math.min(0, Math.max(-maxOffset, startOffset.current + dx))
    setOffset(next)
  }
  const onTouchEnd = () => {
    // Snap open if more than halfway, otherwise closed.
    setOffset(offset < -maxOffset / 2 ? -maxOffset : 0)
    startX.current = null
    setIsDragging(false)
  }

  // Also support mouse drag for desktop / testing.
  const onMouseDown = (e: MouseEvent) => {
    startX.current = e.clientX
    startOffset.current = offset
    setIsDragging(true)
    const move = (ev: globalThis.MouseEvent) => {
      if (startX.current == null) return
      const dx = ev.clientX - startX.current
      const next = Math.min(0, Math.max(-maxOffset, startOffset.current + dx))
      setOffset(next)
    }
    const up = () => {
      setOffset((o) => (o < -maxOffset / 2 ? -maxOffset : 0))
      startX.current = null
      setIsDragging(false)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  const wrapStyle: CSSProperties = { position: 'relative', overflow: 'hidden' }
  const actionsStyle: CSSProperties = {
    position: 'absolute',
    right: 0, top: 0, bottom: 0,
    display: 'flex',
    width: maxOffset,
  }
  const rowStyle: CSSProperties = {
    transform: `translateX(${offset}px)`,
    transition: isDragging ? 'none' : 'transform 200ms',
  }
  const actBtn: CSSProperties = {
    width: ACTION_WIDTH,
    border: 'none',
    color: 'white',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }

  return (
    <div style={wrapStyle}>
      <div style={actionsStyle}>
        {onEdit && (
          <button
            type="button"
            onClick={() => {
              setOffset(0)
              onEdit()
            }}
            style={{ ...actBtn, background: 'var(--color-primary)' }}
          >
            EDIT
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(deleteConfirmText ?? 'Delete this item?')) {
                onDelete()
              }
              setOffset(0)
            }}
            style={{ ...actBtn, background: 'var(--color-danger)' }}
          >
            DELETE
          </button>
        )}
      </div>
      <div
        style={rowStyle}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
      >
        {children}
      </div>
    </div>
  )
}
